import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { trackQuoteRequest } from '../utils/analytics';
import './SimpleQuote.css';
import { ArrowLeft, PaperPlaneTilt } from 'phosphor-react';

const SimpleQuote = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    message: '',
    contactPreference: 'phone'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Preencher formulário com dados do utilizador logado
  useEffect(() => {
    const username = localStorage.getItem('username');
    const email = localStorage.getItem('email');
    const phone = localStorage.getItem('phone');
    const token = localStorage.getItem('token');
    
    if (username && email && token) {
      setFormData(prev => ({
        ...prev,
        name: username,
        email: email,
        phone: phone || ''
      }));
      setIsLoggedIn(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(API_ENDPOINTS.SIMPLE_QUOTE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.status === 'ok') {
        setSubmitStatus({
          type: 'success',
          message: data.message
        });
        
        // Track solicitação de orçamento
        trackQuoteRequest('simple', formData.propertyType);
        
        // Limpar formulário após sucesso
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyType: '',
          message: '',
          contactPreference: 'phone'
        });
        // Navegar para página de obrigado após 2 segundos
        setTimeout(() => {
          navigate('/orcamento/obrigado');
        }, 2000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Erro ao enviar orçamento. Tente novamente.'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar orçamento:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Erro de conexão. Verifique se o servidor está rodando.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="simple-quote-container">
      <div className="simple-quote-content">
        <div className="simple-quote-header">
          <button 
            className="back-button" 
            onClick={() => navigate('/orcamento')}
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
          <h1>Orçamento Simples</h1>
          <p>Descreva as suas necessidades e a nossa equipa entrará em contacto</p>
        </div>

        <form onSubmit={handleSubmit} className="simple-quote-form">
          {submitStatus && (
            <div className={`status-message ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}
          
          <div className="form-section">
            <h2>Dados de Contacto</h2>
            
            <div className="form-group">
              <label htmlFor="name">Nome Completo *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="O seu nome completo"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="seuemail@exemplo.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Telefone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="+351 256 418 202"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Tipo de Propriedade</h2>
            <div className="form-group">
              <label htmlFor="propertyType">Tipo de Propriedade</label>
              <select
                id="propertyType"
                name="propertyType"
                value={formData.propertyType}
                onChange={handleInputChange}
              >
                <option value="">Selecione...</option>
                <option value="casa">Casa/Moradia</option>
                <option value="apartamento">Apartamento</option>
                <option value="loja">Loja/Comércio</option>
                <option value="escritorio">Escritório</option>
                <option value="armazem">Armazém/Indústria</option>
                <option value="outro">Outro</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h2>Descreva as suas Necessidades</h2>
            <div className="form-group">
              <label htmlFor="message">Mensagem *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                placeholder="Descreva detalhadamente o que precisa. Por exemplo:
- Que tipo de segurança pretende (alarme, videovigilância, videoporteiro)?
- Qual o tamanho da propriedade?
- Quantas entradas/saídas tem?
- Tem algum orçamento em mente?
- Prazo pretendido para instalação?
- Outras informações relevantes..."
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Preferência de Contacto</h2>
            <div className="form-group">
              <label>Como prefere ser contactado?</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="contactPreference"
                    value="phone"
                    checked={formData.contactPreference === 'phone'}
                    onChange={handleInputChange}
                  />
                  Telefone
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="contactPreference"
                    value="email"
                    checked={formData.contactPreference === 'email'}
                    onChange={handleInputChange}
                  />
                  Email
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="contactPreference"
                    value="both"
                    checked={formData.contactPreference === 'both'}
                    onChange={handleInputChange}
                  />
                  Ambos
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>Enviando...</>
              ) : (
                <>
                  <PaperPlaneTilt size={20} />
                  Enviar Pedido de Orçamento
                </>
              )}
            </button>
          </div>
        </form>

        <div className="simple-quote-info">
          <h3>O que acontece depois?</h3>
          <ul>
            <li>Recebemos o seu pedido e analisamos as suas necessidades</li>
            <li>A nossa equipa entrará em contacto em 24-48 horas</li>
            <li>Marcamos uma visita técnica gratuita (se necessário)</li>
            <li>Apresentamos uma proposta personalizada</li>
            <li>Agendamos a instalação conforme a sua conveniência</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SimpleQuote;
